import type { MockEnvironment } from '@gotti/test-utils';
import type { PlaygroundRuntimeSelection } from '../types.js';
import { PLAYGROUND_GLOBALS, readPlaygroundGlobal, writePlaygroundGlobal } from '../protocol.js';
import { createPageHandle, type PageHandleLease } from './pageHandle.js';

export interface PlaygroundRuntimeContext {
	selection: PlaygroundRuntimeSelection;
	environment: MockEnvironment | null;
	target: HTMLElement;
	/**
	 * What the definition's `game` builder returned for the suite run in progress, or the last one (`recordPlaygroundGame`):
	 * `undefined` until a run built one, and from the moment the next run starts until its builder resolves.
	 */
	game?: unknown;
}

let currentContext: PlaygroundRuntimeContext | null = null;
const listeners = new Set<(ctx: PlaygroundRuntimeContext) => void>();

/** The page handle published for the current context (`PLAYGROUND_GLOBALS.environment`), or null. */
let currentLease: PageHandleLease | null = null;

/** Where the page handle is published: the page's `window`. Outside a page (node) there is none, and nothing is published. */
const pageScope = (): Record<string, unknown> | null =>
	typeof window === 'undefined' ? null : (window as unknown as Record<string, unknown>);

/** Ends the published page handle, and deletes the global while it still holds that handle. */
const releasePageHandle = (): void => {
	if (!currentLease) return;
	currentLease.release();
	const scope = pageScope();
	if (scope && readPlaygroundGlobal(scope, PLAYGROUND_GLOBALS.environment) === currentLease.handle) delete scope[PLAYGROUND_GLOBALS.environment];
	currentLease = null;
};

/**
 * Publishes the context of the playground that just booted: the runtime's own record, and the page handle a script
 * outside the page reads (`PLAYGROUND_GLOBALS.environment`), which replaces — and ends — the one before it.
 */
export const setCurrentPlaygroundContext = (ctx: PlaygroundRuntimeContext): void => {
	currentContext = ctx;
	releasePageHandle();
	currentLease = createPageHandle(ctx);
	const scope = pageScope();
	if (scope) writePlaygroundGlobal(scope, PLAYGROUND_GLOBALS.environment, currentLease.handle);
	listeners.forEach((listener) => {
		try {
			listener(ctx);
		} catch (err) {
			console.error('[gotti][playground] context listener failed', err);
		}
	});
};

export const getCurrentPlaygroundContext = (): PlaygroundRuntimeContext | null => currentContext;

/**
 * Forget the published context — a host disposing its playground: the page handle is ended and its global deleted.
 * Listeners are not called: there is no context to hand them.
 */
export const clearCurrentPlaygroundContext = (): void => {
	currentContext = null;
	releasePageHandle();
};

/**
 * Records what `selection`'s `game` builder returned for the suite run in progress — `undefined` as a run starts, its
 * value once the builder resolves — on the current context, which the page handle reads. A run of any other selection
 * (one `runPlaygroundTestSuite` was handed directly) records nothing.
 */
export const recordPlaygroundGame = (selection: object, game: unknown): void => {
	if (currentContext?.selection === selection) currentContext.game = game;
};

export const onPlaygroundContext = (
	listener: (ctx: PlaygroundRuntimeContext) => void
): (() => void) => {
	listeners.add(listener);
	if (currentContext) {
		listener(currentContext);
	}
	return () => {
		listeners.delete(listener);
	};
};
